'use client'

import {useEffect} from 'react'
import {PersonalForm, PersonalFormSchema} from '../ui/form'
import {observer} from 'mobx-react'
import React from 'react'
import styled from 'styled-components'

import {Routes} from '@/src/config/routes'
import theme from '@/src/config/theme'

import {PageData} from '@/src/core/domain/entities/page/page-data'

import {Shop} from '@/src/entities/shop'

import {DateForm, DateFormSchema, ShopForm, ShopFormSchema} from './form'
import {FormProvider} from '@/src/shared/modules/form'
import {useFormTesting} from '@/src/features/functional-testing'
import {FormTestingStep} from '@/src/features/functional-testing/store'
import {FormStepsAccordion} from '@/src/features/functional-testing/ui/form-steps-accordion'
import type {FormStepConfig} from '@/src/features/functional-testing/ui/form-steps-accordion'

import {useStore} from '@/src/shared/hooks/useStore'
import {useMediaQuery} from '@/src/shared/hooks/useMediaQuery'
import {getInjection} from '@runlab/di'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
`

export type ShoeTestingPageContent = {
  labs: Shop[]
  bottom_block?: string
}

export type ShoeTestingProps = PageData<ShoeTestingPageContent> & {bottomContent: React.ReactNode}

export const ShoeTestingScreen: React.FC<ShoeTestingProps> = observer(({bottomContent}) => {
  const {pageData} = useStore<ShoeTestingPageContent>()
  const testingFormStore = useFormTesting()

  const isMobile = useMediaQuery(`(max-width: ${theme.mediaSizes.sm}px)`)

  useEffect(() => {
    testingFormStore.init(FormTestingStep.SelectLab, FormTestingStep.SelectLab)
  }, [])

  const {pageData: labsData} = useStore<{labs: Shop[]}>()

  const labStepContent = (
    <FormProvider schema={ShopFormSchema}>
      <ShopForm />
    </FormProvider>
  )

  const dateStepContent = (
    <FormProvider schema={DateFormSchema}>
      <DateForm routes={Routes.webApi.form.shoe} />
    </FormProvider>
  )

  const personalStepContent = (
    <FormProvider
      schema={ShopFormSchema.merge(DateFormSchema).merge(PersonalFormSchema)}
      data={{
        name: pageData?.session?.user?.name,
        last_name: pageData?.session?.user?.last_name,
        phone: pageData?.session?.user?.phone,
        email: pageData?.session?.user?.email,
        agree: true,
        subscribed: true,
      }}>
      <PersonalForm
        routes={Routes.webApi.form.shoe}
        onSuccess={data => {
          getInjection('PulseAnalytics').track('event_signup', {
            eventName: 'Подбор обуви',
            ...data,
          })
        }}
      />
    </FormProvider>
  )

  const steps: FormStepConfig[] = [
    {
      step: FormTestingStep.SelectLab,
      title: 'Лаборатория',
      summary: (() => {
        const lab = labsData!.content.labs.find(lab => lab.id === testingFormStore.shopId)
        return lab ? `${lab.title}, ${lab.address}` : undefined
      })(),
      filled: !!testingFormStore.shopId,
      content: labStepContent,
    },
    {
      step: FormTestingStep.SelectDay,
      title: 'Дата и время',
      summary:
        testingFormStore.date && testingFormStore.time
          ? `${testingFormStore.date.toDate().toLocaleDateString()} в ${testingFormStore.time.title}`
          : undefined,
      filled: !!testingFormStore.date && !!testingFormStore.time,
      content: dateStepContent,
    },
    {
      step: FormTestingStep.Finish,
      title: 'Данные для записи',
      filled: false,
      content: personalStepContent,
    },
  ]

  return (
    <>
      {isMobile ? (
        <Wrapper>
          <FormStepsAccordion steps={steps} />
        </Wrapper>
      ) : (
        <Wrapper>
          {labStepContent}

          {testingFormStore.step > FormTestingStep.SelectLab && dateStepContent}

          {testingFormStore.step > FormTestingStep.SelectDay && personalStepContent}
        </Wrapper>
      )}
      {bottomContent}
    </>
  )
})
